import { createAsyncThunk } from "@reduxjs/toolkit";
import { SlotRepositoryImpl } from "../Data/Repositories/SlotRepositoryImpl";
import { CreateSlotDTO, Slot, SlotSearchParams } from "../Model/Slot";
import { User } from "../../User/Model/User";
import { CreateSlotUseCase } from "../Domain/UseCase/CreateSlotUseCase";
import { UpdateSlotUseCase } from "../Domain/UseCase/UpdateSlotUseCase";
import { DeleteSlotUseCase } from "../Domain/UseCase/DeleteSlotUseCase";
import { ExtendSlotUseCase } from "../Domain/UseCase/ExtendSlotUseCase";
import { BatchExtendSlotUseCase } from "../Domain/UseCase/BatchExtendSlotUseCase";
import { BatchRefundSlotUseCase } from "../Domain/UseCase/BatchRefundSlotUseCase";
import { BatchCreateSlotUseCase } from "../Domain/UseCase/BatchCreateSlotUseCase";
import { BatchUpdateSlotUseCase } from "../Domain/UseCase/BatchUpdateSlotUseCase";
import { CheckSlotsExistsUseCase } from "../Domain/UseCase/CheckSlotsExistsUseCase";
import { GetSlotsUseCase } from "../Domain/UseCase/GetSlotsUseCase";
import { GetSlotsPaginationInfoUseCase } from "../Domain/UseCase/GetSlotsPaginationInfoUseCase";
import { GetSlotSummaryUseCase } from "../Domain/UseCase/GetSlotSummaryUseCase";
import { GetAllSlotsUseCase } from "../Domain/UseCase/GetAllSlotsUseCase";
import { RefundSlotUseCase } from "../Domain/UseCase/RefundSlotUseCase";
import { GetSlotRankHistoryUseCase } from "../Domain/UseCase/GetSlotRankHistoryUseCase";
import { BatchUpdateSlotRequest, CheckSlotsExistsRequest } from "../Model/Slot";

const repo = new SlotRepositoryImpl();

/* ────────────────────────────────────────── */
/* CRUD */
export const createSlot = createAsyncThunk(
  "slot/create",
  async ({
    dto,
    me,
  }: {
    dto: CreateSlotDTO;
    me: User;
    tempId?: number;
    optimisticApplied?: boolean;
  }) => new CreateSlotUseCase(repo).execute(dto, me)
);

export const batchCreateSlots = createAsyncThunk(
  "slot/batchCreate",
  async ({
    request,
    me,
  }: {
    request: { count: number; slots: CreateSlotDTO[] };
    me: User;
    tempIds?: number[];
    optimisticApplied?: boolean;
  }) => new BatchCreateSlotUseCase(repo).execute(request, me)
);

export const updateSlot = createAsyncThunk(
  "slot/update",
  async ({
    id,
    dto,
    me,
  }: {
    id: number;
    dto: Partial<Slot>;
    me: User;
    optimisticApplied?: boolean;
  }) =>
    new UpdateSlotUseCase(repo).execute(id, dto, me)
);

export const deleteSlot = createAsyncThunk(
  "slot/delete",
  async ({
    id,
    me,
  }: {
    id: number;
    me: User;
    optimisticApplied?: boolean;
  }) => new DeleteSlotUseCase(repo).execute(id, me)
);

/* ────────────────────────────────────────── */
/*   상태 변경   */
export const extendSlot = createAsyncThunk(
  "slot/extend",
  async ({ id, days, me }: { id: number; days: number; me: User }) =>
    new ExtendSlotUseCase(repo).execute(id, days, me)
);

export const refundSlot = createAsyncThunk(
  "slot/refund",
  async ({ id, me }: { id: number; me: User }) =>
    new RefundSlotUseCase(repo).execute(id, me)
);

/* ────────────────────────────────────────── */
/*   일괄 처리   */
export const batchExtend = createAsyncThunk(
  "slot/batchExtend",
  async ({
    ids,
    days,
    me,
    optimisticApplied,
  }: {
    ids: number[];
    days: number;
    me: User;
    optimisticApplied?: boolean;
  }) =>
    new BatchExtendSlotUseCase(repo).execute(ids, days, me)
);

export const batchRefund = createAsyncThunk(
  "slot/batchRefund",
  async ({
    ids,
    me,
    optimisticApplied,
  }: {
    ids: number[];
    me: User;
    optimisticApplied?: boolean;
  }) =>
    new BatchRefundSlotUseCase(repo).execute(ids, me)
);

export const batchUpdateSlots = createAsyncThunk(
  "slot/batchUpdate",
  async ({ request, me }: { request: BatchUpdateSlotRequest; me: User }) =>
    new BatchUpdateSlotUseCase(repo).execute(request, me)
);

export const checkSlotsExists = createAsyncThunk(
  "slot/checkExists",
  async ({ request }: { request: CheckSlotsExistsRequest }) =>
    new CheckSlotsExistsUseCase(repo).execute(request)
);

/* ────────────────────────────────────────── */
/*   조회   */
export const fetchSlots = createAsyncThunk(
  "slot/fetch",
  async ({ me, params }: { me: User; params?: SlotSearchParams }, { signal }) =>
    new GetSlotsUseCase(repo).execute(me, params, signal)
);

export const fetchSlotsPaginationInfo = createAsyncThunk(
  "slot/fetchPaginationInfo",
  async ({ me, params }: { me: User; params?: SlotSearchParams }) => 
    new GetSlotsPaginationInfoUseCase(repo).execute(me, params)
);

export const fetchSlotSummary = createAsyncThunk("slot/summary", async () =>
  new GetSlotSummaryUseCase(repo).execute()
);

/* ────────────────────────────────────────── */
/*   전체 조회   */
export const fetchAllSlots = createAsyncThunk(
  "slot/fetchAll",
  async ({ me, active }: { me: User; active?: boolean }) => 
    new GetAllSlotsUseCase(repo).execute(me, active)
);

/* ────────────────────────────────────────── */
/*   rank_history (모달에서만 사용, store에 저장하지 않음)   */
export const fetchSlotRankHistory = createAsyncThunk(
  "slot/fetchRankHistory",
  async ({ me, slotId }: { me: User; slotId: number }) =>
    new GetSlotRankHistoryUseCase(repo).execute(me, slotId)
);
